/**
 * AR Technical Demo (F0 / GEN-2256) — scene-checkpoint snapshots.
 *
 * THROWAWAY DEMO CODE. Non-linear entry is a hard requirement (storyboard §2b):
 * the presenter must be able to drop into ANY flow/scene at any time and have
 * the world it needs stand up cold. Each checkpoint mutates the fixture store
 * (via `updateArDemoState`) to apply that scene's PREREQUISITE state, then the
 * scene's own beat performs the visible action.
 *
 * Checkpoints are CUMULATIVE within a flow and idempotent: applying a later
 * scene's checkpoint applies everything needed up to that point, so re-firing
 * is safe and entering "file the claim" stands up the validated deductions +
 * backups + opened threads beneath it. The shared dataset stays consistent so
 * Flow 3 reconciles Flow 1's remittance regardless of entry order.
 *
 * Reset (`resetArDemoState`) wipes every checkpoint mutation back to cold open.
 *
 * The checkpoint functions are registered as no-arg demo tools (see
 * `registerTechnicalDemoTools`) so the scripted interceptor fires them by name
 * before the scene's narration; each flow's ENTRY prompt fires the first
 * checkpoint of that flow.
 */

import { updateArDemoState } from './fixture-store';
import type { ArDemoFixtureState } from './fixture-store.types';
import {
  TECH_ID,
  buildShortPayThread,
  buildDeductionThread,
  buildShortPayEvidence,
  buildDeductionEvidence,
  buildDeductionEvidenceInitial,
  buildDeductionBackupDocuments,
} from './fixture-seed-technical';

// ---------------------------------------------------------------------------
// Activity-feed helpers (Flow 2 portal beats). The agent's portal actions are
// logged on deduction thread COL-1043 as they happen, so the timeline tells the
// story. `payload.event` is the node label the timeline renders (see
// CollectionThreadActivityTimeline.describe).
// ---------------------------------------------------------------------------
const isoNow = (): string => new Date().toISOString();
const isoInMinutes = (minutes: number): string =>
  new Date(Date.now() + minutes * 60_000).toISOString();

/** Tomorrow as a short label (e.g. "Jun 26") for the next-day re-check note. */
function nextDayLabel(): string {
  return new Date(Date.now() + 86_400_000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function appendDeductionActivity(
  draft: ArDemoFixtureState,
  entry: { id: string; event: string; body: string; at: string },
): void {
  const thread = draft.threadsById[TECH_ID.deductionThread];
  if (!thread) return;
  thread.updatedAt = entry.at;
  thread.activities.push({
    id: entry.id,
    threadId: TECH_ID.deductionThread,
    kind: 'system_event',
    body: entry.body,
    payload: { event: entry.event },
    actorUserId: null,
    actorName: null,
    createdAt: entry.at,
    updatedAt: entry.at,
  });
}

// ---------------------------------------------------------------------------
// FLOW 1 — remittance matched → short-pay rebuttal with evidence.
// ---------------------------------------------------------------------------

/**
 * Scene 2 prerequisite: the remittance is matched / cash-applied. INV-48811 and
 * INV-48812 match in full (→ "Confirmed"); INV-48810 matches but is short $1,250
 * on its own line, no reason cited (→ "Disputed", Flow 1). The $3,100 chargeback
 * rides in as a separate line that names no invoice, so it stays unmatched until
 * the portal backup attributes it to INV-48812 (Flow 2). Updates the remittance
 * line items + list summary so the real RemittanceDetails page (F1) renders the
 * matched state cold.
 */
export function applyRemittanceMatched(): void {
  updateArDemoState((draft) => {
    // Reveal the Scene-2 "Issues flagged" callout — the discrepancies only
    // surface once the agent has reconciled the advice (cold open hides them).
    draft.technicalUi.remittanceProcessed = true;
    const remittance = draft.remittancesById[TECH_ID.remittance];
    if (!remittance) return;
    // The three invoice lines match an invoice (status `matched`). INV-48810 is
    // itself short on its own line ($23,750 vs $25,000) → badged "Disputed" by
    // the demo (see useDemoDisputes, driven off the line vs its invoice net).
    // INV-48811/48812 are paid in full → "Confirmed". The chargeback rides in as
    // a SEPARATE line that names no invoice, so it stays `unmatched` — at this
    // screen we can't yet attribute it (the portal backup later ties it to
    // INV-48812). This keeps the advice reconciling and mirrors the 4-line PDF.
    for (const line of remittance.lineItems) {
      if (line.kind === 'deduction') {
        line.matchStatus = 'unmatched';
        line.matchReason =
          'Unattributed chargeback — names no invoice on the advice';
        continue;
      }
      line.matchStatus = 'matched';
      if (line.id === TECH_ID.rli48810) {
        line.matchReason = 'Short pay — $1,250 withheld, no reason cited';
      } else {
        line.matchReason = 'Exact invoice + amount match — paid in full';
      }
    }
    const listItem = draft.remittanceList.find(
      (r) => r.id === TECH_ID.remittance,
    );
    if (listItem) {
      listItem.lineItemSummary = {
        total: 4,
        matched: 3,
        autoMatched: 0,
        unmatched: 1,
        ambiguous: 0,
        rejected: 0,
      };
    }
  });
}

/**
 * Scene 3 prerequisite: the short-pay auto-opens collection thread COL-1042.
 * Implies the remittance is matched (scene 2). Seeds the short-pay thread +
 * its assembled evidence (invoice → SO → Sentinel shipment → BOL/POD → 2
 * internal emails), each provenance-linked to its source inbound email.
 */
export function applyShortPayThreadOpened(): void {
  applyRemittanceMatched();
  updateArDemoState((draft) => {
    draft.threadsById[TECH_ID.shortPayThread] = buildShortPayThread();
    draft.threadEvidenceById[TECH_ID.shortPayThread] = buildShortPayEvidence();
  });
}

/**
 * Scene 5 prerequisite: the rebuttal is ready to draft. Implies the short-pay
 * thread is open with full evidence (scenes 2-4). No extra state beyond the
 * opened thread is needed — F2 builds the draft dialog from the thread +
 * evidence + the seeded rep (Dana Reyes, dana.reyes@pillar.com) for the CC.
 */
export function applyRebuttalReady(): void {
  applyShortPayThreadOpened();
}

// ---------------------------------------------------------------------------
// FLOW 2 — deductions → portal backups → contract validation → file a claim.
// ---------------------------------------------------------------------------

/**
 * Scene 6 prerequisite: the deductions open into their own thread COL-1043,
 * each in `pending_validation`. Implies the remittance is matched (scene 2).
 * The thread opens as a BARE chargeback — no invoice or supporting documents
 * linked yet; those are assembled only after the portal backup is pulled
 * (`applyPortalBackupsFiled`), so the cold-open evidence is the minimal set.
 */
export function applyDeductionThreadOpened(): void {
  applyRemittanceMatched();
  updateArDemoState((draft) => {
    draft.threadsById[TECH_ID.deductionThread] = buildDeductionThread();
    draft.threadEvidenceById[TECH_ID.deductionThread] =
      buildDeductionEvidenceInitial();
    for (const deduction of draft.deductions) {
      deduction.status = 'pending_validation';
      deduction.invalidAmount = null;
      deduction.backupDocumentUrls = [];
      deduction.filedClaimConfirmation = null;
    }
  });
}

/**
 * Scenes 8-9 prerequisite: the portal backups have been pulled and filed onto
 * the deduction thread. Implies the deduction thread is open (scene 6).
 *
 * Pulling the backup is what IDENTIFIES the claim: the receiving report ties
 * chargeback #CR-DED-99214 to INV-48812 and states the basis (a claimed 10%
 * shortage). Only now can we assemble + link our refuting evidence, so this
 * swaps the thread's minimal cold-open evidence for the resolved set (invoice +
 * signed POD/BOL + shipment), scopes the thread to INV-48812, files the backup
 * documents, and stamps the backup URLs onto Deduction B.
 */
export function applyPortalBackupsFiled(): void {
  applyDeductionThreadOpened();
  const backups = buildDeductionBackupDocuments();
  updateArDemoState((draft) => {
    // The backup reveals the invoice + basis, so link our full evidence now.
    const evidence = buildDeductionEvidence();
    const existing = new Set(evidence.documents.map((d) => d.id));
    for (const doc of backups) {
      if (!existing.has(doc.id)) evidence.documents.push(doc);
    }
    draft.threadEvidenceById[TECH_ID.deductionThread] = evidence;

    // Scope the thread to INV-48812 now that the backup identifies it.
    const thread = draft.threadsById[TECH_ID.deductionThread];
    if (thread) {
      thread.invoiceId = TECH_ID.inv48812;
      thread.title = 'Deduction — INV-48812 chargeback #CR-DED-99214 ($3,100)';
      thread.description =
        'Crestline’s portal backup ties chargeback #CR-DED-99214 to INV-48812 and claims a 10% shortage ($3,100). Our signed POD/BOL show a full, clean delivery — linked here to evaluate the claim.';
    }

    const dedB = draft.deductions.find((d) => d.id === TECH_ID.deductionB);
    if (dedB) {
      dedB.backupDocumentUrls = backups
        .map((d) => d.storagePath)
        .filter((p): p is string => p !== null);
    }
    // Log the portal run on the thread's activity feed. Safe to re-run: the
    // chain above rebuilds the thread fresh each call (applyDeductionThreadOpened),
    // so this never accumulates duplicates.
    appendDeductionActivity(draft, {
      id: 'tech-act-deduction-portal-pull',
      event:
        'Pulled #CR-DED-99214 backup from Crestline’s portal — identifies INV-48812, claimed 10% shortage; filed to COL-1043',
      body: 'Nami operated Crestline’s vendor portal and downloaded chargeback #CR-DED-99214’s notice. It ties the chargeback to INV-48812 and claims a 10% shortage ($3,100). Nami filed the backup onto the thread, scoped it to INV-48812, and linked our signed POD/BOL for evaluation.',
      at: isoNow(),
    });
  });
}

/**
 * Scene 10 prerequisite: the chargeback has been validated against the portal
 * backup + our delivery proof. The backup claims a 10% shortage on INV-48812,
 * but the signed POD/BOL show a full, clean delivery — so the entire $3,100 is
 * unsupported. Implies the backups are filed (scene 9). Sets Deduction B's
 * `invalidAmount` + status so the validation card renders the result cold.
 */
export function applyDeductionValidated(): void {
  applyPortalBackupsFiled();
  updateArDemoState((draft) => {
    const dedB = draft.deductions.find((d) => d.id === TECH_ID.deductionB);
    if (dedB) {
      dedB.invalidAmount = '3100.00';
      dedB.status = 'validated';
    }
  });
}

/**
 * Scene 11 prerequisite/outcome: the dispute claim is filed back on Crestline's
 * portal for the $3,100 unsupported chargeback. Implies the deduction is
 * validated (scene 10). Stamps the portal confirmation number + flips status.
 */
export function applyClaimFiled(): void {
  applyDeductionValidated();
  updateArDemoState((draft) => {
    const dedB = draft.deductions.find((d) => d.id === TECH_ID.deductionB);
    if (dedB) {
      dedB.status = 'claim_filed';
      dedB.filedClaimConfirmation = 'CR-CLAIM-44190';
    }
    // Log the filed claim, then the scheduled next-day re-check, on the thread.
    // Timestamps step forward so they sort newest-first above the portal pull.
    appendDeductionActivity(draft, {
      id: 'tech-act-deduction-claim-filed',
      event:
        'Filed dispute claim on Crestline’s portal — $3,100.00, confirmation #CR-CLAIM-44190',
      body: 'Nami filed a dispute claim on Crestline’s vendor portal for the full $3,100.00 unsupported chargeback #CR-DED-99214, attaching the signed POD and BOL. Portal confirmation #CR-CLAIM-44190.',
      at: isoInMinutes(1),
    });
    appendDeductionActivity(draft, {
      id: 'tech-act-deduction-followup',
      event: `Follow-up scheduled — Nami will re-check the portal for a response on ${nextDayLabel()}`,
      body: `Claim #CR-CLAIM-44190 is awaiting Crestline’s response. Nami will re-check the vendor portal for activity on ${nextDayLabel()} and surface any update on this thread.`,
      at: isoInMinutes(2),
    });
  });
}

// ---------------------------------------------------------------------------
// FLOW 3 — cash receipt ingestion → bank reconciliation.
// ---------------------------------------------------------------------------

/**
 * Scenes 12-13 prerequisite: today's deposits are synced and the Crestline ACH
 * deposit reconciles to RA-7741 (→ its three invoices). The exception deposit
 * stays `needs_review`. Independent of Flow 1/2 entry order: the deposit links
 * to the same remittance id either way.
 */
export function applyBankDepositsReconciled(): void {
  updateArDemoState((draft) => {
    const crestline = draft.bankTransactions.find(
      (t) => t.id === TECH_ID.bankCrestline,
    );
    if (crestline) {
      crestline.matchedRemittanceId = TECH_ID.remittance;
      crestline.matchConfidence = 98;
      crestline.status = 'matched';
      crestline.exceptionReason = null;
    }
    // Mark the linked remittance as bank-cleared now that the funds match.
    const remittance = draft.remittancesById[TECH_ID.remittance];
    if (remittance) remittance.bankCleared = true;
    // The Crestline cash receipt moves out of the review queue.
    const receipt = draft.cashReceiptsById[TECH_ID.cashReceipt];
    if (receipt) receipt.status = 'matched';
    const receiptListItem = draft.cashReceiptList.find(
      (r) => r.id === TECH_ID.cashReceipt,
    );
    if (receiptListItem) receiptListItem.status = 'matched';
  });
}

/**
 * Scene 14 prerequisite/outcome: the exception deposit is resolved (split into
 * its two customers). Implies the clean deposits are reconciled (scene 13).
 * Flips the exception deposit to matched so the view shows a clean board.
 */
export function applyExceptionResolved(): void {
  applyBankDepositsReconciled();
  updateArDemoState((draft) => {
    const exception = draft.bankTransactions.find(
      (t) => t.id === TECH_ID.bankException,
    );
    if (exception) {
      exception.status = 'matched';
      exception.matchConfidence = 90;
      exception.exceptionReason = null;
    }
  });
}

/**
 * Blanket cash-receipt ingestion run: flips the run to `complete` so the
 * `CashReceiptIngestionPage` reveals the per-deposit results (cash receipts
 * created, remittances matched, short-pays + exceptions flagged). The outcomes
 * already live on each deposit in the seed; this just exposes them — so firing
 * it from the script lands the same end state the on-page "Run" button does.
 */
export function applyCashReceiptsIngested(): void {
  updateArDemoState((draft) => {
    draft.cashReceiptIngestion.runState = 'complete';
  });
}

/** The full set of named checkpoints, for the tool registrar + tests. */
export const TECHNICAL_CHECKPOINTS: Record<string, () => void> = {
  applyRemittanceMatched,
  applyShortPayThreadOpened,
  applyRebuttalReady,
  applyDeductionThreadOpened,
  applyPortalBackupsFiled,
  applyDeductionValidated,
  applyClaimFiled,
  applyBankDepositsReconciled,
  applyExceptionResolved,
  applyCashReceiptsIngested,
};

/** A safe no-op typed reference, used where a checkpoint may be absent. */
export type CheckpointFn = (state: ArDemoFixtureState) => void;
